import { useEffect, useState } from 'react'

import { useLocale, useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import { farmWallClockToInstant } from '../../utils/farmTime'
import { loadLotStockAt, parseArrivalCount, parseMoveDeaths, planFishMove } from '../../utils/lotFishMove'

// Findings #178 / #183 — the part of "Trasladar lote" that is new: the day and
// time the fish were moved, and, when the destination unit already holds the
// same lot, how many fish join it. Findings #179: when the whole lot goes to a
// unit without that lot, "Cantidad que llega" (prefilled with the lot's stock
// at the move date) becomes its count in the new unit. Findings #204: fish
// that die during the move ("Murieron en el traslado") are a mortality of the
// unit they leave, not a smaller count.

export type FishMoveStocks = { source: number | null; target: number | null }

type UnitRef = { farmId: string; farmName?: string; productionUnitId: string; productionUnitName?: string }

const field = { display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 } as const
const input = { padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' } as const

const formatCount = (value: number | null, locale: string): string => (value === null ? '—' : value.toLocaleString(locale))

export default function MoveFields(props: {
  /** `<input type="datetime-local">` value, farm time. */
  moveAt: string
  onMoveAtChange: (value: string) => void
  timeZone: string
  disabled?: boolean
  sourceLot: Lot
  source: UnitRef
  /** The same lot already living in the destination unit, when there is one. */
  sameNameTarget?: { lot: Lot; unit: UnitRef } | null
  fishMoved: string
  onFishMovedChange: (value: string) => void
  onStocksChange: (stocks: FishMoveStocks | null) => void
  /** Destination unit of a whole-lot move (no same lot there); null otherwise. */
  arrivalUnit?: UnitRef | null
  arrivalCount: string
  onArrivalCountChange: (value: string) => void
  diedInMove: string
  onDiedInMoveChange: (value: string) => void
}) {
  const t = useT()
  const locale = useLocale()
  const [stocks, setStocks] = useState<FishMoveStocks | null>(null)
  const { sameNameTarget, onStocksChange } = props
  const moveAtIso = farmWallClockToInstant(props.moveAt, props.timeZone)
  const targetLotId = sameNameTarget?.lot.id ?? ''

  useEffect(() => {
    if (!sameNameTarget || !moveAtIso) {
      setStocks(null)
      onStocksChange(null)
      return
    }
    let cancelled = false
    setStocks(null)
    onStocksChange(null)
    void Promise.all([
      loadLotStockAt({ ...props.source, lotId: props.sourceLot.id, asOf: moveAtIso }),
      loadLotStockAt({ ...sameNameTarget.unit, lotId: sameNameTarget.lot.id, asOf: moveAtIso })
    ]).then(([source, target]) => {
      if (cancelled) return
      setStocks({ source, target })
      onStocksChange({ source, target })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveAtIso, props.sourceLot.id, targetLotId])

  // Findings #179: the lot's stock at the move date prefills "Cantidad que llega".
  const { arrivalUnit, onArrivalCountChange } = props
  const arrivalUnitId = arrivalUnit?.productionUnitId ?? ''
  const [arrivalSourceStock, setArrivalSourceStock] = useState<number | null | undefined>(undefined)
  // Until the user types an arrival count, it follows the stock less the deaths.
  const [arrivalTouched, setArrivalTouched] = useState(false)
  const deaths = parseMoveDeaths(props.diedInMove)
  const died = deaths.ok ? deaths.count : 0
  useEffect(() => {
    if (!arrivalUnitId || !moveAtIso) {
      setArrivalSourceStock(undefined)
      return
    }
    let cancelled = false
    setArrivalSourceStock(undefined)
    setArrivalTouched(false)
    void loadLotStockAt({ ...props.source, lotId: props.sourceLot.id, asOf: moveAtIso }).then((stock) => {
      if (cancelled) return
      setArrivalSourceStock(stock)
      onArrivalCountChange(stock === null ? '' : String(Math.max(0, stock - died)))
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveAtIso, props.sourceLot.id, arrivalUnitId])
  useEffect(() => {
    if (arrivalTouched || typeof arrivalSourceStock !== 'number') return
    onArrivalCountChange(String(Math.max(0, arrivalSourceStock - died)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [died])

  const expectedArrival = typeof arrivalSourceStock === 'number' ? Math.max(0, arrivalSourceStock - died) : null
  const arrival = parseArrivalCount(props.arrivalCount)
  const arrivalShortBy = expectedArrival !== null && arrival.ok && arrival.count !== null && arrival.count < expectedArrival
    ? expectedArrival - arrival.count
    : 0

  const plan = stocks && props.fishMoved.trim()
    ? planFishMove({ fishMoved: props.fishMoved, sourceStock: stocks.source, targetStock: stocks.target, diedInMove: died })
    : null
  const sourceName = props.source.productionUnitName ?? '—'
  const targetName = sameNameTarget?.unit.productionUnitName ?? '—'

  const diedField = (
    <label style={field}>
      {t('lots.move.died.label')}
      <input
        type="text"
        inputMode="numeric"
        value={props.diedInMove}
        onChange={(event) => props.onDiedInMoveChange(event.target.value)}
        disabled={props.disabled}
        placeholder="0"
        style={input}
        data-testid="lots-move-died"
      />
      <span style={{ fontSize: 12, fontWeight: 400, color: deaths.ok ? '#475569' : '#b91c1c' }}>
        {deaths.ok ? t('lots.move.died.help', { unit: sourceName }) : t('lots.move.died.invalid')}
      </span>
    </label>
  )

  return (
    <div style={{ display: 'grid', gap: 12 }} data-testid="lots-move-fields">
      <label style={field}>
        {t('lots.move.dateLabel')}
        <input
          type="datetime-local"
          value={props.moveAt}
          onChange={(event) => props.onMoveAtChange(event.target.value)}
          disabled={props.disabled}
          style={input}
          data-testid="lots-move-date"
        />
        <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{t('lots.move.dateHelp')}</span>
      </label>

      {sameNameTarget ? (
        <div
          data-testid="lots-move-into-same-lot"
          style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 10, border: '1px solid #99f6e4', backgroundColor: '#f0fdfa', fontSize: 13, color: '#0f172a' }}
        >
          <strong>{t('lots.move.sameLot.title', { unit: targetName, lot: sameNameTarget.lot.lotKey })}</strong>
          {diedField}
          <label style={field}>
            {t('lots.move.sameLot.fishLabel')}
            <input
              type="text"
              inputMode="numeric"
              value={props.fishMoved}
              onChange={(event) => props.onFishMovedChange(event.target.value)}
              disabled={props.disabled}
              style={input}
              data-testid="lots-move-fish-count"
            />
            <span style={{ fontSize: 12, fontWeight: 400, color: '#475569' }}>
              {stocks === null
                ? t('lots.move.sameLot.loadingStock')
                : stocks.source === null
                  ? t('lots.move.sameLot.sourceStockUnknown', { unit: sourceName })
                  : t('lots.move.sameLot.sourceStock', { count: formatCount(stocks.source, locale), unit: sourceName })}
            </span>
          </label>
          {plan && plan.ok ? (
            <div data-testid="lots-move-fish-preview">
              {died > 0 ? `${t('lots.move.died.previewDeaths', { count: formatCount(died, locale), unit: sourceName })} · ` : ''}
              {t('lots.move.sameLot.preview', {
                source: sourceName,
                sourceBefore: formatCount(stocks?.source ?? null, locale),
                sourceAfter: formatCount(plan.sourceAfter, locale),
                target: targetName,
                targetBefore: formatCount(stocks?.target ?? null, locale),
                targetAfter: formatCount(plan.targetAfter, locale)
              })}
            </div>
          ) : null}
          {plan && !plan.ok ? (
            <div role="alert" style={{ color: '#b91c1c' }}>
              {t(plan.error === 'fishMoreThanSource' ? (died > 0 ? 'lots.move.error.fishAndDeathsMoreThanSource' : 'lots.move.error.fishMoreThanSource') : 'lots.move.error.fishRequired', {
                count: formatCount(stocks?.source ?? null, locale),
                unit: sourceName
              })}
            </div>
          ) : null}
          {stocks && stocks.target === null ? (
            <div style={{ color: '#92400e' }}>{t('lots.move.sameLot.targetStockUnknown', { unit: targetName })}</div>
          ) : null}
          <div style={{ fontSize: 12, color: '#475569' }}>
            {plan && plan.ok && plan.closesSource
              ? t('lots.move.sameLot.closesSource', { unit: sourceName, target: targetName })
              : t('lots.move.sameLot.help', { unit: sourceName })}
          </div>
        </div>
      ) : null}

      {arrivalUnit && !sameNameTarget ? diedField : null}

      {arrivalUnit && !sameNameTarget ? (
        <label style={field}>
          {t('lots.move.arrival.label', { unit: arrivalUnit.productionUnitName ?? '—' })}
          <input
            type="text"
            inputMode="numeric"
            value={props.arrivalCount}
            onChange={(event) => { setArrivalTouched(true); onArrivalCountChange(event.target.value) }}
            disabled={props.disabled || arrivalSourceStock === undefined}
            style={input}
            data-testid="lots-move-arrival-count"
          />
          <span style={{ fontSize: 12, fontWeight: 400, color: '#475569' }}>
            {arrivalSourceStock === undefined
              ? t('lots.move.sameLot.loadingStock')
              : arrivalSourceStock === null
                ? t('lots.move.arrival.helpUnknown', { unit: arrivalUnit.productionUnitName ?? '—' })
                : t('lots.move.arrival.help', { count: formatCount(arrivalSourceStock, locale), unit: sourceName })}
          </span>
        </label>
      ) : null}

      {arrivalUnit && !sameNameTarget && arrivalShortBy > 0 ? (
        <div data-testid="lots-move-arrival-short" style={{ display: 'grid', gap: 6, padding: 12, borderRadius: 10, border: '1px solid #fcd34d', backgroundColor: '#fffbeb', fontSize: 13, color: '#0f172a' }}>
          <span>{t('lots.move.arrival.short', {
            arrival: formatCount(arrival.ok ? arrival.count : null, locale),
            expected: formatCount(expectedArrival, locale),
            missing: formatCount(arrivalShortBy, locale)
          })}</span>
          <div>
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => props.onDiedInMoveChange(String(died + arrivalShortBy))}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #b45309', background: '#fff', color: '#92400e', cursor: 'pointer', fontWeight: 600 }}
              data-testid="lots-move-arrival-short-died"
            >
              {t('lots.move.arrival.shortDied', { missing: formatCount(arrivalShortBy, locale) })}
            </button>
          </div>
          <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.move.arrival.shortCount')}</span>
        </div>
      ) : null}
    </div>
  )
}
